import { maxDateToday, minDate36Mois } from '@workspace/utils';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OUI_NON_STRING_OPTIONS } from './options';
import { Question } from '../../question';
import { FieldInput, FieldPatternInput, FieldRadioGroup, FieldSelectNative } from '../../smart-form';
import { useAutoNext } from '../use-auto-next';
import { Cancel, Warning } from '../../../icons-amo';

/*
 * Contenus de questions (à placer dans un <Question> de l'app). Portage de legacy `@amo/core/components/questions`
 * SuspensionPermis, InformationsSuspensionPermis, AnnulationPermis, InformationsAnnulationPermis,
 * CondamnationAlcoolemie, TauxAlcool, CondamnationStupefiants, CondamnationDelitFuite, CondamnationConduiteSansAssurance.
 */

const ANTECEDENTS = 'DemandeTarif.Antecedents';

export const ANTECEDENTS_PERMIS_FIELDS = {
  SUSPENSION: `${ANTECEDENTS}.SuspensionPermis`,
  DATE_SUSPENSION: `${ANTECEDENTS}.DateSuspensionPermis`,
  NB_MOIS_SUSPENSION: `${ANTECEDENTS}.NbMoisSuspensionPermis`,
  ANNULATION: `${ANTECEDENTS}.AnnulationPermis`,
  DATE_ANNULATION: `${ANTECEDENTS}.DateAnnulationPermis`,
  ALCOOLEMIE: `${ANTECEDENTS}.CondamnationAlcoolemie`,
  TX_ALCOOL: `${ANTECEDENTS}.TxAlcool`,
  STUPEFIANTS: `${ANTECEDENTS}.CondamnationStupefiants`,
  DELIT_FUITE: `${ANTECEDENTS}.CondamnationDelitFuite`,
  SANS_ASSURANCE: `${ANTECEDENTS}.CondamnationConduiteSansAssurance`,
} as const;

const F = ANTECEDENTS_PERMIS_FIELDS;
const CONDAMNATIONS = [F.ALCOOLEMIE, F.STUPEFIANTS, F.DELIT_FUITE, F.SANS_ASSURANCE];

/** Mois de suspension, de 60 à 1 (legacy nbMonthList(61)). */
const NB_MOIS_OPTIONS = Array.from({ length: 60 }, (_, i) => {
  const nb = String(60 - i);
  return { value: nb, label: `${nb} mois` };
});

const SOUS_TITRE_3_ANS = 'Au cours des 3 dernières années...';

/** Suspension de permis. « Non » vide la date / durée et remet les condamnations à '0' sauf si annulation. */
export function SuspensionPermisContent() {
  const { ref, next } = useAutoNext();
  const { setValue, getValues } = useFormContext();
  const suspension = useWatch({ name: F.SUSPENSION });

  useEffect(() => {
    if (suspension !== '0') return;
    setValue(F.DATE_SUSPENSION, '');
    setValue(F.NB_MOIS_SUSPENSION, '');
    if (getValues(F.ANNULATION) !== '1') CONDAMNATIONS.forEach((name) => setValue(name, '0'));
  }, [suspension, setValue, getValues]);

  return (
    <div ref={ref} className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Warning />
        <Question.SubTitle>{SOUS_TITRE_3_ANS}</Question.SubTitle>
        <Question.Title>
          <b>Votre permis</b> a-t-il fait l&apos;objet <b>d&apos;une suspension</b> ?
        </Question.Title>
      </Question.Header>
      <FieldRadioGroup name={F.SUSPENSION} options={OUI_NON_STRING_OPTIONS} onClick={next} />
    </div>
  );
}

/** Date et durée de la suspension (affiché si SuspensionPermis = '1'). */
export function InformationsSuspensionPermisContent() {
  return (
    <div className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Warning />
        <Question.Title>
          Indiquez <b>la date de suspension</b> de votre permis et le <b>nombre de mois de suspension</b>
        </Question.Title>
      </Question.Header>
      <div className="w-full max-w-sm space-y-4">
        <FieldPatternInput
          type="date"
          name={F.DATE_SUSPENSION}
          label="Permis suspendu le"
          validation={{ validate: { maxDateToday, minDate36Mois } }}
        />
        <FieldSelectNative
          name={F.NB_MOIS_SUSPENSION}
          label="Nombre de mois de suspension"
          placeholder="Nombre de mois de suspension"
          options={NB_MOIS_OPTIONS}
        />
      </div>
    </div>
  );
}

/** Annulation de permis. « Non » vide la date et remet les condamnations à '0' si pas de suspension. */
export function AnnulationPermisContent() {
  const { ref, next } = useAutoNext();
  const { setValue, getValues } = useFormContext();
  const annulation = useWatch({ name: F.ANNULATION });

  useEffect(() => {
    if (annulation !== '0') return;
    setValue(F.DATE_ANNULATION, '');
    // why: iso legacy, asymétrique avec la suspension (=== '0' ici, !== '1' côté suspension)
    if (getValues(F.SUSPENSION) === '0') CONDAMNATIONS.forEach((name) => setValue(name, '0'));
  }, [annulation, setValue, getValues]);

  return (
    <div ref={ref} className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Cancel />
        <Question.SubTitle>{SOUS_TITRE_3_ANS}</Question.SubTitle>
        <Question.Title>
          <b>Votre permis</b> a-t-il fait l&apos;objet <b>d&apos;une annulation</b> ?
        </Question.Title>
      </Question.Header>
      <FieldRadioGroup name={F.ANNULATION} options={OUI_NON_STRING_OPTIONS} onClick={next} />
    </div>
  );
}

/** Date d'annulation (affiché si AnnulationPermis = '1'). */
export function InformationsAnnulationPermisContent() {
  return (
    <div className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <Cancel />
        <Question.Title>
          Indiquez la <b>date d&apos;annulation</b> de votre permis
        </Question.Title>
      </Question.Header>
      <div className="w-full max-w-sm">
        <FieldPatternInput
          type="date"
          name={F.DATE_ANNULATION}
          label="Permis annulé le"
          validation={{ validate: { maxDateToday, minDate36Mois } }}
        />
      </div>
    </div>
  );
}

/**
 * Motifs de suspension / annulation (affiché si suspension ou annulation = '1'), regroupés en une question.
 * « Non » à l'alcoolémie vide le taux.
 */
export function CondamnationsContent() {
  const { setValue } = useFormContext();
  const alcoolemie = useWatch({ name: F.ALCOOLEMIE });

  const alcoolemieOptions = OUI_NON_STRING_OPTIONS.map((option) =>
    option.value === '0' ? { ...option, onChange: () => setValue(F.TX_ALCOOL, null) } : option,
  );

  return (
    <div className="w-full flex flex-col items-center space-y-6">
      <FieldRadioGroup
        name={F.ALCOOLEMIE}
        label={
          <>
            Avez vous eu <b>une annulation</b> ou <b>une suspension</b> pour motif <b>d&apos;alcoolémie</b> ?
          </>
        }
        options={alcoolemieOptions}
      />
      {alcoolemie === '1' && (
        <FieldInput
          name={F.TX_ALCOOL}
          type="number"
          // why: sans step, la validation native refuserait les décimales (ex. 0,5)
          step="any"
          label={
            <>
              Indiquez le <b>taux en mg/L</b> d&apos;air expulsé
            </>
          }
          placeholder="Exemple : 0,5"
        />
      )}
      <FieldRadioGroup
        name={F.STUPEFIANTS}
        label={
          <>
            Avez vous eu <b>une annulation</b> ou <b>une suspension</b> pour motif de <b>stupéfiants</b> ?
          </>
        }
        options={OUI_NON_STRING_OPTIONS}
      />
      <FieldRadioGroup
        name={F.DELIT_FUITE}
        label={
          <>
            Avez vous eu <b>une annulation</b> ou <b>une suspension</b> pour motif de <b>délit de fuite</b> ?
          </>
        }
        options={OUI_NON_STRING_OPTIONS}
      />
      <FieldRadioGroup
        name={F.SANS_ASSURANCE}
        label={
          <>
            Avez-vous eu une <b>annulation</b> ou une <b>suspension</b> pour motif de conduite sans assurance ?
          </>
        }
        options={OUI_NON_STRING_OPTIONS}
      />
    </div>
  );
}
